/**
 * Permite generar un PDF de informe de mantención.
 * Para el taller mecánico. Transkyrios. Servikyrios.
 * 
 * Paleta Transkyrios → azul marino (#1B2C4E) + gris medio (#6B7280) + blanco
 * Paleta Servikyrios → burdeo (#7B1C2A) + negro (#1A1A1A) + blanco
 * Paleta default  → azul oscuro neutro (#1F3A5F) + gris
 */
import pdfMake from "../pdfMakeConfig";
import { formatFecha } from "../dateFormatter";
import logoK from "../kyrioscoB64";
import logoTK from "../transkyriosB64";
import logoSK from "../servikyriosB64";

// Paletas por empresa
const TEMAS = {
  Transkyrios: {
    primario:    '#000061',
    secundario:  '#a9b2ce',
    acento:      '#000061',
    texto:       '#1A1A1A',
    textSuave:   '#6B7280',
    fondoHeader: '#F0F4F9',
    fondoFila:   '#F0F4F9',
    lineaDivisor:'#000082',
    rojo:        '#e73a3a',
    textoTitulo: '#011347',
    textoSubTitulo: '#7c7c7c',
  },
  Servikyrios: {
    primario:    '#5e0505',
    secundario:  '#9b2323',
    acento:      '#7c0000',
    texto:       '#1A1A1A',
    textSuave:   '#5A5A5A',
    fondoHeader: '#000000',
    fondoFila:   '#f9f0f0',
    lineaDivisor:'#640000',
    rojo:        '#5e0505',
    textoTitulo: '#ffffff',
    textoSubTitulo: '#b7b7b7',
  },
  default: {
    primario:    '#1F3A5F',
    secundario:  '#92a7bf',
    acento:      '#011347',
    texto:       '#1A1A1A',
    textSuave:   '#6B7280',
    fondoHeader: '#F0F4F9',
    fondoFila:   '#F0F4F9',
    lineaDivisor:'#1F3A5F',
    rojo:        '#b00202',
    textoTitulo: '#011347',
    textoSubTitulo: '#7c7c7c',
  }
};

const LOGOS = {
  Transkyrios: {
    image: logoTK,
    width: 170,
    margin: [-15, 15, 0, 0]
  },
  Servikyrios: {
    image: logoSK,
    width: 120,
    margin: [0, 2, 0, 0]
  },
  default: {
    image: logoK,
    width: 120,
    margin: [0, 12, 0, 0]
  }
};

export const generarPDF = (datos) => {
  const { cliente, vehiculo, servicio, evaluaciones, evidencias, numeroOT } = datos;
  const fechaImpresion = formatFecha(new Date());
  const fechaIngreso   = formatFecha(vehiculo.fechaIngreso);
  const fechaSalida    = formatFecha(vehiculo.fechaSalida);
  const repuestos      = servicio.repuestos || [];
  const tieneMec2      = servicio.mecanico2 && servicio.mecanico2.length > 0;

  const empresa = servicio?.empresa;
  const tema =  TEMAS[empresa] || TEMAS.default;
  const logoConfig = LOGOS[empresa] || LOGOS.default;

  function chunkArray(arr, size) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  }

  // ─────────────────────────────────────────────
  // Helper: título de sección con banda de color
  // ─────────────────────────────────────────────
  const seccionTitulo = (texto) => ({
    table: {
      widths: ['*'],
      body: [[{
        text: texto.toUpperCase(),
        fontSize: 8.5,
        bold: true,
        color: '#FFFFFF',
        fillColor: tema.primario,
        margin: [6, 4, 6, 4],
        letterSpacing: 0.8,
      }]]
    },
    layout: 'noBorders',
    margin: [0, 12, 0, 4],
  });

  // ─────────────────────────────────────────────
  // Helper: separador fino
  // ─────────────────────────────────────────────
  const separador = (margenTop = 6, margenBot = 6) => ({
    canvas: [{
      type: 'line', x1: 0, y1: 0, x2: 515, y2: 0,
      lineWidth: 0.4, lineColor: '#D1D5DB'
    }],
    margin: [0, margenTop, 0, margenBot]
  });

  // ─────────────────────────────────────────────
  // Layout: tabla info dos columnas con fila alterna
  // ─────────────────────────────────────────────
  const layoutAlternado = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    fillColor: (rowIndex) => rowIndex % 2 === 1 ? tema.fondoFila : null,
    paddingLeft:   () => 6,
    paddingRight:  () => 6,
    paddingTop:    () => 4,
    paddingBottom: () => 4,
  };

  // ─────────────────────────────────────────────
  // Layout: tabla de evaluación con header
  // ─────────────────────────────────────────────
  const layoutEval = {
    hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 0.5 : 0,
    hLineColor: () => '#D1D5DB',
    vLineWidth: () => 0,
    fillColor: (rowIndex) => {
      if (rowIndex === 0) return tema.secundario;
      return rowIndex % 2 === 0 ? tema.fondoFila : null;
    },
    paddingLeft:   () => 6,
    paddingRight:  () => 6,
    paddingTop:    () => 4,
    paddingBottom: () => 4,
  };

  // ─────────────────────────────────────────────
  // Layout: tabla de cliente con divisor central
  // ─────────────────────────────────────────────
  const layoutCliente = {
    hLineWidth: () => 0,
    vLineWidth: (i, node) => (i === Math.floor(node.table.widths.length / 2)) ? 0.5 : 0,
    vLineColor: () => '#D1D5DB',
    fillColor: (rowIndex) => rowIndex % 2 === 1 ? tema.fondoFila : null,
    paddingLeft:   () => 6,
    paddingRight:  () => 6,
    paddingTop:    () => 4,
    paddingBottom: () => 4,
  };

  // ─────────────────────────────────────────────
  // Filas de vehículo con kilométrajes destacados
  // ─────────────────────────────────────────────
  const filasVehiculo = [
    [{ text: 'Patente',  color: tema.textSuave, fontSize: 9 }, { text: vehiculo.patente,  fontSize: 9 }],
    [{ text: 'Marca',    color: tema.textSuave, fontSize: 9 }, { text: vehiculo.marca,    fontSize: 9 }],
    [{ text: 'Modelo',   color: tema.textSuave, fontSize: 9 }, { text: vehiculo.modelo,   fontSize: 9 }],
    [{ text: 'Año',      color: tema.textSuave, fontSize: 9 }, { text: String(vehiculo.anio), fontSize: 9 }],
    [{ text: 'N° Motor', color: tema.textSuave, fontSize: 9 }, { text: vehiculo.nmotor || '-', fontSize: 9 }],
    [{ text: 'N° Serie', color: tema.textSuave, fontSize: 9 }, { text: vehiculo.nserie || '-', fontSize: 9 }],
    [
      { text: 'Kilometraje Actual',            bold: true, color: tema.rojo, fontSize: 9 },
      { text: String(vehiculo.kilometrajeAct), bold: true, color: tema.rojo, fontSize: 9 }
    ],
    [
      { text: 'Próxima Mantención (km)',        bold: true, color: tema.rojo, fontSize: 9 },
      { text: String(vehiculo.kilometrajeProx), bold: true, color: tema.rojo, fontSize: 9 }
    ],
    [{ text: 'Combustible', color: tema.textSuave, fontSize: 9 }, { text: `${vehiculo.combustible}%`, fontSize: 9 }],
  ];

  // ─────────────────────────────────────────────
  // DEFINICIÓN DEL DOCUMENTO
  // ─────────────────────────────────────────────
  const docDefinition = {
    pageSize:    'letter', 
    pageMargins: [40, 110, 40, 70],
    
    header: {
      stack: [
        // Banda superior con color primario
        {
          canvas: [{
            type: 'rect',
            x: 0, y: 0,
            w: 612, h: 100,
            color: tema.fondoHeader,
          }]
        },
        // Contenido del header sobre la banda
        {
          margin: [40, -96, 40, 0],
          columns: [
            // Logo
            {
              width: 130,
              stack: [
                {
                  image: logoConfig.image,
                  width: logoConfig.width,
                  margin: logoConfig.margin
                }
              ]
            },
            // Título central
            {
              width: '*',
              stack: [
                {
                  text: 'INFORME DE VEHÍCULO',
                  fontSize: 17,
                  bold: true,
                  color: tema.textoTitulo,
                  margin: [0, 30, 0, 2]
                },
                {
                  text: 'TALLER MECÁNICO  ·  REVISIÓN DE VEHÍCULO',
                  fontSize: 8,
                  color: tema.textoSubTitulo,
                  letterSpacing: 1,
                  margin: [0, 0, 0, 0]
                },
              ],
              alignment: 'center',
            },
            // OT y fecha
            {
              width: 120,
              stack: [
                {
                  text: `N° OT`,
                  fontSize: 7,
                  color: tema.textoSubTitulo,
                  alignment: 'right',
                  margin: [0, 30, 0, 1]
                },
                {
                  text: datos.numeroOT || numeroOT || 'TMXX-OT-000000',
                  fontSize: 10,
                  bold: true,
                  color: tema.textoTitulo,
                  alignment: 'right',
                  margin: [0, 0, 0, 6]
                },
                {
                  text: fechaImpresion,
                  fontSize: 8,
                  color: tema.textoSubTitulo,
                  alignment: 'right',
                  margin: [0, -5, 0, 10]
                },
              ]
            },
          ]
        },
        // Banda acento delgada bajo el header
        {
          canvas: [{
            type: 'rect',
            x: 0, y: 0,
            w: 612, h: 5,
            color: tema.lineaDivisor,
          }],
          margin: [0, 3, 0, 0]
        },
      ]
    },
    
    // ── FOOTER ──────────────────────────────────
    footer: (currentPage, pageCount) => ({
      margin: [40, 0, 40, 20],
      columns: [
        {
          text: 'Documento generado automáticamente — uso interno',
          fontSize: 7.5,
          color: '#9CA3AF',
          alignment: 'left',
          margin: [0, 8, 0, 0]
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          fontSize: 8,
          color: tema.textSuave,
          bold: true,
          alignment: 'right',
          margin: [0, 8, 0, 0]
        }
      ]
    }),

    // ── CONTENIDO ───────────────────────────────
    content: [

      // ── MECÁNICO + FECHAS (columnas) ──────────
      {
        columns: [
          {
            width: '50%',
            stack: [
              seccionTitulo('Mecánico asignado'),
              {
                table: {
                  widths: ['45%', '55%'],
                  body: [
                    tieneMec2
                      ? [{ text: 'Primer Mecánico', color: tema.textSuave, fontSize: 9 }, { text: servicio.mecanico || '-', fontSize: 9 }]
                      : [{ text: 'Nombre',          color: tema.textSuave, fontSize: 9 }, { text: servicio.mecanico || '-', fontSize: 9 }],
                    tieneMec2
                      ? [{ text: 'Segundo Mecánico', color: tema.textSuave, fontSize: 9 }, { text: servicio.mecanico2 || '-', fontSize: 9 }]
                      : null,
                    [{ text: 'Servicio Realizado', color: tema.textSuave, fontSize: 9 }, { text: servicio.tipo || '-', fontSize: 9 }],
                  ].filter(Boolean)
                },
                layout: layoutAlternado,
              }
            ]
          },
          {
            width: '50%',
            stack: [
              seccionTitulo('Recepción - Salida'),
              {
                table: {
                  widths: ['45%', '55%'],
                  body: [
                    [{ text: 'Fecha de Ingreso', color: tema.textSuave, fontSize: 9 }, { text: fechaIngreso || 'Sin Fecha', fontSize: 9 }],
                    [{ text: 'Fecha de Salida',  color: tema.textSuave, fontSize: 9 }, { text: fechaSalida  || 'Sin Fecha', fontSize: 9 }],
                  ]
                },
                layout: layoutAlternado,
              }
            ]
          }
        ],
        columnGap: 16,
      },

      // ── VEHÍCULO ──────────────────────────────
      seccionTitulo('Información del vehículo'),
      {
        table: {
          widths: ['35%', '65%'],
          body: filasVehiculo,
        },
        layout: layoutAlternado,
      },

      // ── SÍNTOMAS ──────────────────────────────
      seccionTitulo('Síntomas del vehículo'),
      {
        table: {
          widths: ['*'],
          body: [[{
            text: vehiculo.sintomas || 'Sin observaciones entregadas por el propietario respecto al estado o funcionamiento del vehículo.',
            fontSize: 9,
            color: vehiculo.sintomas ? tema.texto : tema.textSuave,
            italics: !vehiculo.sintomas,
            margin: [4, 4, 4, 4],
          }]]
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 0.5 : 0,
          hLineColor: () => '#D1D5DB',
          vLineWidth: () => 0,
          paddingLeft: () => 6, paddingRight: () => 6,
          paddingTop: () => 4, paddingBottom: () => 4,
        }
      },

      // ── EVALUACIÓN DE DAÑOS ───────────────────
      ...(evaluaciones && evaluaciones.length > 0
        ? [
            seccionTitulo('Evaluación de daños'),
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: [
                  [
                    { text: 'Elemento',  fontSize: 9, bold: true, color: '#FFFFFF' },
                    { text: 'Estado', fontSize: 9, bold: true, color: '#FFFFFF' }
                  ],
                  ...evaluaciones.map(ev => [
                    { text: ev.nombre, fontSize: 9 },
                    { text: ev.estado, fontSize: 9 }
                  ])
                ]
              },
              layout: layoutEval,
            }
          ]
        : [
            seccionTitulo('Evaluación de daños'),
            { text: 'Sin evaluación registrada.', fontSize: 9, color: tema.textSuave, italics: true, margin: [6, 2, 0, 6] }
          ]
      ),

      // ── OBSERVACIONES GENERALES ───────────────
      seccionTitulo('Observaciones generales'),
      {
        table: {
          widths: ['*'],
          body: [[{
            text: servicio.observaciones || 'Sin observaciones.',
            fontSize: 9,
            color: servicio.observaciones ? tema.texto : tema.textSuave,
            italics: !servicio.observaciones,
            margin: [4, 4, 4, 4],
          }]]
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 0.5 : 0,
          hLineColor: () => '#D1D5DB',
          vLineWidth: () => 0,
          paddingLeft: () => 6, paddingRight: () => 6,
          paddingTop: () => 4, paddingBottom: () => 4,
        }
      },

      // ── DIAGNÓSTICO Y SEGUIMIENTO ─────────────
      seccionTitulo('Diagnóstico y seguimiento técnico'),
      {
        stack: [
          {
            stack: [
              { text: 'Diagnóstico preliminar', fontSize: 8.5, bold: true, color: tema.primario, margin: [0, 4, 0, 2] },
              { text: servicio.diagnostico || '—', fontSize: 9, color: tema.texto, margin: [0, 0, 0, 6] },
            ],
            pageBreak: 'avoid'
          },
          separador(),
          {
            stack: [
              { text: 'Trabajo recomendado', fontSize: 8.5, bold: true, color: tema.primario, margin: [0, 4, 0, 2] },
              { text: servicio.recomendacion || '—', fontSize: 9, color: tema.texto, margin: [0, 0, 0, 6] },
            ],
            pageBreak: 'avoid'
          },
          separador(),
          {
            stack: [
              { text: 'Última reparación registrada', fontSize: 8.5, bold: true, color: tema.primario, margin: [0, 4, 0, 2] },
              { text: servicio.ultimaReparacion || '—', fontSize: 9, color: tema.texto, margin: [0, 0, 0, 6] },
            ],
            pageBreak: 'avoid'
          },
          separador(),
          {
            stack: [
              { text: 'Observaciones del mecánico', fontSize: 8.5, bold: true, color: tema.primario, margin: [0, 4, 0, 2] },
              { text: servicio.obsMecanico || 'Sin observaciones.', fontSize: 9, color: servicio.obsMecanico ? tema.texto : tema.textSuave, italics: !servicio.obsMecanico, margin: [0, 0, 0, 4] },
            ],
            pageBreak: 'avoid'
          },
        ],
        margin: [8, 0, 8, 4],
      },

      // ── REPUESTOS ─────────────────────────────
      ...(repuestos && repuestos.length > 0
        ? [
            seccionTitulo('Repuestos utilizados'),
            {
              table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'Nombre del repuesto', fontSize: 9, bold: true, color: '#FFFFFF' },
                    { text: 'Cantidad',             fontSize: 9, bold: true, color: '#FFFFFF', alignment: 'center' },
                    { text: 'Unidad',               fontSize: 9, bold: true, color: '#FFFFFF', alignment: 'center' },
                  ],
                  ...repuestos.map(rep => [
                    { text: rep.nombre || '',                            fontSize: 9 },
                    { text: rep.cantidad?.toString() || '',              fontSize: 9, alignment: 'center' },
                    { text: rep.unidad || '',                            fontSize: 9, alignment: 'center' },
                  ])
                ],
              },
              layout: layoutEval,
            }
          ]
        : []
      ),

      // ── EVIDENCIA FOTOGRÁFICA ─────────────────
      ...(evidencias && evidencias.length > 0
        ? [
            seccionTitulo('Evidencia fotográfica'),
            ...chunkArray(evidencias, 2).map(filaImgs => ({
              columns: filaImgs.map(img => ({
                stack: [
                  { image: img, width: 245, margin: [0, 0, 0, 2] },
                ]
              })),
              columnGap: 12,
              margin: [0, 2, 0, 8]
            }))
          ]
        : []
      ),

      // ── CLIENTE ───────────────────────────────
      seccionTitulo('Información del cliente'),
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              { text: 'Nombre',  color: tema.textSuave, fontSize: 9 },
              { text: cliente.nombre,           fontSize: 9 },
              { text: 'RUT',     color: tema.textSuave, fontSize: 9 },
              { text: cliente.rut,              fontSize: 9 },
            ],
            [
              { text: 'Correo',  color: tema.textSuave, fontSize: 9 },
              { text: cliente.correo  || '-',   fontSize: 9 },
              { text: 'Celular', color: tema.textSuave, fontSize: 9 },
              { text: cliente.celular || '-',   fontSize: 9 },
            ],
          ]
        },
        layout: layoutCliente,
      },

      // ── TÉRMINOS Y CONDICIONES ─────────────────
      seccionTitulo('Términos y condiciones'),
      {
        ol: [
          'La firma proporcionada será utilizada únicamente para la generación del documento oficial en formato PDF y no será almacenada en nuestros sistemas.',
          'El taller mecánico no se hace responsable por pérdidas o daños de objetos de valor que permanezcan dentro del vehículo durante su permanencia en las instalaciones.',
          'El taller no se hace responsable por daños o fallas que el vehículo ya tuviera antes de ingresar. Estos se dejarán registrados en el informe de recepción.',
          'Al firmar este documento, el cliente declara haber leído, comprendido y aceptado los presentes términos y condiciones.',
        ],
        fontSize: 8.5,
        color: '#6B7280',
        alignment: 'justify',
        margin: [4, 0, 4, 6],
      },

      // ── FIRMAS ────────────────────────────────
      ...((datos.firmas?.cliente && datos.firmas.cliente.length > 0) ||
          (datos.firmas?.mecanico && datos.firmas.mecanico.length > 0)
        ? [
            seccionTitulo('Firmas'),
            {
              columns: [
                // Firma cliente
                {
                  width: '*',
                  stack: [
                    { text: 'Firma del cliente', fontSize: 8.5, bold: true, color: tema.primario, alignment: 'center', margin: [0, 4, 0, 4] },
                    datos.firmas.cliente
                      ? {
                          table: {
                            widths: ['*'],
                            body: [[{
                              image: datos.firmas.cliente,
                              width: 230, height: 75,
                              alignment: 'center',
                              margin: [0, 4, 0, 4],
                            }]]
                          },
                          layout: {
                            hLineWidth: () => 0.5, vLineWidth: () => 0.5,
                            hLineColor: () => '#D1D5DB', vLineColor: () => '#D1D5DB',
                          }
                        }
                      : { text: 'Sin firma registrada', fontSize: 9, color: tema.textSuave, italics: true, alignment: 'center', margin: [0, 20, 0, 20] }
                  ]
                },
                // Firma mecánico
                {
                  width: '*',
                  stack: [
                    { text: 'Firma del mecánico', fontSize: 8.5, bold: true, color: tema.primario, alignment: 'center', margin: [0, 4, 0, 4] },
                    datos.firmas.mecanico
                      ? {
                          table: {
                            widths: ['*'],
                            body: [[{
                              image: datos.firmas.mecanico,
                              width: 230, height: 75,
                              alignment: 'center',
                              margin: [0, 4, 0, 4],
                            }]]
                          },
                          layout: {
                            hLineWidth: () => 0.5, vLineWidth: () => 0.5,
                            hLineColor: () => '#D1D5DB', vLineColor: () => '#D1D5DB',
                          }
                        }
                      : { text: 'Sin firma registrada', fontSize: 9, color: tema.textSuave, italics: true, alignment: 'center', margin: [0, 20, 0, 20] }
                  ]
                },
              ],
              columnGap: 20,
              pageBreak: 'avoid',
            }
          ]
        : []
      ),
    ],

    // ── ESTILOS ───────────────────────────────────
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        alignment: 'center',
      },
      subheader: {
        fontSize: 12,
        bold: true,
        margin: [0, 10, 0, 5],
      },
      tableSection: {
        margin: [0, 5, 0, 10],
        fontSize: 10,
        color: '#000',
      },
    },
    defaultStyle: {
      font: 'Roboto',
    },
  };

  return pdfMake.createPdf(docDefinition);
};

// Layout con línea divisora central (exportado para compatibilidad)
export const hline = {
  hLineWidth: (i, node) => {
    if (i === 0) return 0.8;
    if (i === node.table.headerRows) return 0.8;
    return 0.5;
  },
  hLineColor: (i, node) => {
    if (i === 0 || i === node.table.headerRows) return '#000';
    return '#999';
  },
  vLineWidth: (i, node) => {
    const total = node.table.widths.length;
    if (i === Math.floor(total / 2)) return 1;
    if (i === 0 || i === total) return 0.8;
    return 0;
  },
  vLineColor: (i, node) => {
    const total = node.table.widths.length;
    if (i === Math.floor(total / 2)) return '#000';
    return '#FFF';
  },
  paddingLeft:   () => 5,
  paddingRight:  () => 5,
  paddingTop:    () => 5,
  paddingBottom: () => 5,
};
